// use-eve-profile — koppelt den eve-profile-store an die aktuelle userId aus
// dem auth-store, lädt aus Backend wenn vorhanden, ansonsten aus localStorage.
//
// V1-Pendant: loadEveProfile/saveEveProfile/loadProfileFromSupabase/syncProfileToSupabase
// aus useEveProfile.js.

import { useCallback, useEffect, useMemo } from 'react';
import { loadProfileFromBackend, syncProfileToBackend } from '@/lib/eve-profile-api';
import { formatEveProfilePromptBlock } from '@/lib/eve-profile';
import { selectAccessToken, selectProfile, useAuthStore } from '@/stores/auth-store';
import { useEveProfileStore } from '@/stores/eve-profile-store';
import type { EveProfile } from '@/types/eve-profile';

export interface UseEveProfileReturn {
  profile: EveProfile;
  userId: string | null;
  /** Aktualisiert das Profil lokal + persistiert + syncht ins Backend. */
  update: (patch: Partial<EveProfile>) => Promise<EveProfile>;
  /** Komplette Profile ersetzen + persistieren + syncen. */
  replace: (profile: EveProfile) => Promise<void>;
  /** Reset auf Defaults. */
  reset: () => Promise<void>;
  /** Pflichtblock für EVE-System-Prompt. */
  promptBlock: string;
  /** Erzwingt einen Pull vom Backend (z.B. nach Login). */
  pullFromBackend: () => Promise<void>;
}

export function useEveProfile(): UseEveProfileReturn {
  const authProfile = useAuthStore(selectProfile);
  const accessToken = useAuthStore(selectAccessToken);
  const userId = authProfile?.id ?? null;

  const storeUserId = useEveProfileStore((s) => s.userId);
  const profile = useEveProfileStore((s) => s.profile);
  const loadForUser = useEveProfileStore((s) => s.loadForUser);
  const updateStore = useEveProfileStore((s) => s.update);
  const replaceStore = useEveProfileStore((s) => s.replace);
  const resetStore = useEveProfileStore((s) => s.reset);

  // Sync auth-userId → store-userId und versuche Backend-Pull
  useEffect(() => {
    if (userId === storeUserId) return;
    loadForUser(userId);
    if (userId && accessToken) {
      void loadProfileFromBackend(accessToken).then((remote) => {
        if (remote && useEveProfileStore.getState().userId === userId) {
          replaceStore(remote);
        }
      });
    }
  }, [userId, storeUserId, accessToken, loadForUser, replaceStore]);

  const promptBlock = useMemo(() => formatEveProfilePromptBlock(profile), [profile]);

  const update = useCallback<UseEveProfileReturn['update']>(
    async (patch) => {
      const next = updateStore(patch);
      if (accessToken) {
        void syncProfileToBackend(next, accessToken);
      }
      return next;
    },
    [updateStore, accessToken],
  );

  const replace = useCallback<UseEveProfileReturn['replace']>(
    async (newProfile) => {
      replaceStore(newProfile);
      if (accessToken) {
        await syncProfileToBackend(newProfile, accessToken);
      }
    },
    [replaceStore, accessToken],
  );

  const reset = useCallback<UseEveProfileReturn['reset']>(async () => {
    resetStore();
    if (accessToken) {
      await syncProfileToBackend(useEveProfileStore.getState().profile, accessToken);
    }
  }, [resetStore, accessToken]);

  const pullFromBackend = useCallback<UseEveProfileReturn['pullFromBackend']>(async () => {
    if (!accessToken) return;
    const remote = await loadProfileFromBackend(accessToken);
    if (remote) replaceStore(remote);
  }, [accessToken, replaceStore]);

  return { profile, userId, update, replace, reset, promptBlock, pullFromBackend };
}
